import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DropdownItem {
  label: string;
  value: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface DropdownProps {
  items: DropdownItem[];
  value?: string;
  placeholder?: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  value,
  placeholder = 'Select an option',
  onSelect,
  disabled = false,
  className = '',
  style = {},
  size = 'md',
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectedItem = items.find(item => item.value === value);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (buttonRef.current)
        setMenuRect(buttonRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  const getSizeClasses = () =>
    size === 'sm'
      ? 'h-8 px-2 text-sm'
      : size === 'lg'
        ? 'h-12 px-4 text-lg'
        : 'h-10 px-3';
  const handleSelect = (v: string) => {
    onSelect(v);
    setIsOpen(false);
  };

  const dropdownClasses = ['relative', fullWidth ? 'w-full' : 'w-48', className]
    .filter(Boolean)
    .join(' ');
  const buttonClasses = [
    'flex items-center justify-between w-full border rounded-lg transition-colors',
    getSizeClasses(),
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
  ]
    .filter(Boolean)
    .join(' ');
  const buttonStyles: React.CSSProperties = {
    backgroundColor: 'var(--bg-primary)',
    borderColor: 'var(--border)',
    color: 'var(--text-primary)',
    ...style,
  };

  const portalMenu =
    isOpen && menuRect
      ? createPortal(
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 99998 }}
              onClick={() => setIsOpen(false)}
            />
            <div
              style={{
                position: 'fixed',
                top: menuRect.bottom + 4,
                left: menuRect.left,
                width: menuRect.width,
                zIndex: 99999,
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                maxHeight: '15rem',
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '0.25rem 0',
              }}
            >
              {items.map(item => (
                <button
                  key={item.value}
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                    opacity: item.disabled ? 0.5 : 1,
                    color: item.disabled
                      ? 'var(--text-tertiary)'
                      : 'var(--text-primary)',
                    fontSize: size === 'sm' ? '0.875rem' : '1rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  onClick={() => !item.disabled && handleSelect(item.value)}
                  disabled={item.disabled}
                  onMouseEnter={e => {
                    if (!item.disabled)
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--bg-secondary)';
                  }}
                  onMouseLeave={e => {
                    if (!item.disabled)
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'transparent';
                  }}
                >
                  {item.icon && (
                    <span style={{ flexShrink: 0 }}>{item.icon}</span>
                  )}
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.label}
                  </span>
                  {value === item.value && (
                    <svg
                      style={{
                        width: '1rem',
                        height: '1rem',
                        flexShrink: 0,
                        color: 'var(--accent)',
                      }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div className={dropdownClasses}>
      <button
        ref={buttonRef}
        type="button"
        className={buttonClasses}
        style={buttonStyles}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        onFocus={e => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent)20';
          }
        }}
        onBlur={e => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        onMouseEnter={e => {
          if (!disabled) e.currentTarget.style.borderColor = 'var(--accent)';
        }}
        onMouseLeave={e => {
          if (!disabled && !isOpen)
            e.currentTarget.style.borderColor = 'var(--border)';
        }}
      >
        <div className="flex items-center gap-2">
          {selectedItem?.icon && selectedItem.icon}
          <span
            style={{
              color: selectedItem
                ? 'var(--text-primary)'
                : 'var(--text-secondary)',
            }}
          >
            {selectedItem ? selectedItem.label : placeholder}
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {portalMenu}
    </div>
  );
};

export default Dropdown;
